import { clientColumns, TableSettings, useTableSettings, withTableSettings } from '../../features';
import {
  ClientFilter,
  DataGrid,
  FilterBadges,
  ListPageWrapper,
  PageTitle, useAgent,
  useClients, useSessionStorageState
} from '../../shared';
import { useClientListFilterDrawer, withClientListFilterDrawer } from '../clients/ui/client-list-filter-drawer';
import { useClientsToCall } from '../clients/hooks/use-clients-to-call';
import {
  ClientViewFilterInput,
  ClientViewSortInput,
  Field,
  PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import _ from 'lodash';
import { Button, Stack } from '@mui/material';
import { SearchInput } from '@coverright/ui/inputs';
import { FunnelSimple } from '@phosphor-icons/react';
import * as React from 'react';

const Page = () => {
  const [filters, setFilters] = useFilters();
  const {columns} = useTableSettings(clientColumns, filters, settings => setFilters(settings.filters));
  const [page, setPage] = usePage();
  const [sort, setSort] = useSort();
  const {clients, loading, total} = useClients(page, filters, sort, 'directmail');
  const {open} = useClientListFilterDrawer();
  const agent = useAgent();

  const clientsToCall = useClientsToCall();

  const openFilters = () => open(v => {
    setFilters(v)
  }, filters)

  const onFilterRemoveClick = (filterName: keyof ClientViewFilterInput) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  }

  return (
    <ListPageWrapper>

      <PageTitle title={`Direct mail clients`}>
        <TableSettings />
        <Stack direction={'row'} gap={2}>
          <SearchInput value={filters?.searchTerm || ''}
                       loading={loading}
                       onChange={searchTerm => setFilters(p => ({...p, searchTerm}))} />
          <Button variant={'outlined'}
                  data-test={'clients-filter-button'}
                  onClick={openFilters}
                  startIcon={<FunnelSimple weight={'bold'} />}
                  size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
            Filters
          </Button>
        </Stack>
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filters} nameMapFunc={filterNameFunc} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={total}
                getRowClassName={row => (
                  clientsToCall.some(c => c.clientId === row.clientId) ?
                    clientsToCall.find(c => c.clientId === row.clientId)?.currentAssignment?.assignedAgentId === agent?.id ? 'assigned-client-data-grid-row' : 'assigned-client-to-other-agent-data-grid-row'
                    : ''
                )}
                loading={loading}
                sort={sort}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={clients}/>
    </ListPageWrapper>
  );
};

export const DirectMail = withClientListFilterDrawer(withTableSettings(Page, 'directMail'));

const filterNameFunc = (name: keyof ClientFilter) => {
  switch (name) {
    case 'followUpDate': return 'Next contact date';
    case 'range': return 'Created';
    default: return name;
  }
}

const useFilters = () => {
  return useSessionStorageState<ClientFilter | undefined>(
    'directMailFilters',
    undefined
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'directMailPage',
    { page: 0, size: 20 }
  );
};

const useSort = () => {
  return useSessionStorageState<ClientViewSortInput | undefined>(
    'directMailSort',
    {field: Field.CreatedAt, direction: SortDirection.Desc}
  )
}
